import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Activity,
  XCircle,
  Link2,
  Loader2
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';

interface Position {
  id: number;
  symbol: string;
  direction: string;
  qty: number;
  entry_price: number;
  current_price: number;
  pnl: number;
  pnl_currency: string;
}

interface Trade {
  id: number;
  date: string;
  symbol: string;
  direction: string;
  entry_price: number;
  exit_price: number | null;
  qty: number;
  pnl: number;
  pnl_currency: string;
  label: string;
}

interface Balance {
  balance: number;
  currency: string;
  realized_pnl_today: number;
  unrealized_pnl: number;
}

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || '';
}

async function apiFetch(path: string, options?: RequestInit) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    },
  });
  return res.json();
}

const TradingAccount = () => {
  const [position, setPosition] = useState<Position | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCredentials, setHasCredentials] = useState(true);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    loadAll();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const interval = position ? 5000 : 30000;
    intervalRef.current = window.setInterval(() => {
      apiFetch('/api/trading/position').then(data => {
        if (data.reason === 'no_credentials') return;
        setPosition(data.position || null);
      }).catch(() => {});
    }, interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [position !== null]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [balData, posData, histData] = await Promise.all([
        apiFetch('/api/trading/balance'),
        apiFetch('/api/trading/position'),
        apiFetch('/api/trading/history?limit=50'),
      ]);

      if (posData.reason === 'no_credentials' || balData.reason === 'no_credentials') {
        setHasCredentials(false);
        setLoading(false);
        return;
      }

      setBalance(balData.balance !== undefined ? balData : null);
      setPosition(posData.position || null);
      setTrades(histData.trades || []);
      setOffset(histData.trades?.length || 0);

      if (balData.error) setError(balData.error);
      else if (posData.error) setError(posData.error);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      const result = await apiFetch('/api/trading/close-position', { method: 'POST' });
      if (result.error) {
        setError(result.error);
      } else {
        setPosition(null);
        await loadAll();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setClosing(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await apiFetch(`/api/trading/history?limit=50&offset=${offset}`);
      if (data.trades?.length) {
        setTrades(prev => [...prev, ...data.trades]);
        setOffset(prev => prev + data.trades.length);
      }
    } catch {} finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasCredentials) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className="card text-center py-12">
          <Link2 className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 mb-2">Conecta tu cuenta de broker para ver tus operaciones</p>
          <Link
            to="/dashboard/broker-connection"
            className="btn-primary inline-block mt-4"
          >
            Ir a configuración
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Activity className="h-5 w-5 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-white">Mi Cuenta</h1>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg border bg-red-900/20 border-red-900/50 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* SECCIÓN 1: Balance */}
      {balance && (
        <div className="card mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-400">Balance</p>
              <p className="text-xl font-bold text-white flex items-center">
                <Wallet className="h-4 w-4 mr-1 text-gray-400" />
                ${balance.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">P&L hoy</p>
              <p className={`text-xl font-bold flex items-center ${
                balance.realized_pnl_today >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {balance.realized_pnl_today >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                ${Math.abs(balance.realized_pnl_today).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">P&L no realizado</p>
              <p className={`text-xl font-bold ${
                balance.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                ${balance.unrealized_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2, signDisplay: 'always' })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN 2: Posición abierta */}
      <div className="card mb-6">
        <h2 className="text-sm font-medium text-gray-400 mb-3">Posición abierta</h2>
        {position ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white font-semibold">
                  {position.symbol} · {position.direction} · {position.qty} contrato{position.qty > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-gray-400">
                  Entrada: ${position.entry_price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <p className={`text-2xl font-bold ${
                position.pnl >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                ${position.pnl?.toLocaleString('en-US', { minimumFractionDigits: 2, signDisplay: 'always' })}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={closing}
              className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {closing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <span>{closing ? 'Cerrando...' : 'Cerrar posición'}</span>
            </button>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Sin posición abierta</p>
        )}
      </div>

      {/* SECCIÓN 3: Historial */}
      <div className="card">
        <h2 className="text-sm font-medium text-gray-400 mb-3">Historial de operaciones</h2>
        {trades.length === 0 ? (
          <p className="text-gray-500 text-sm">Sin operaciones registradas</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-white/5">
                    <th className="text-left py-2">Fecha</th>
                    <th className="text-left py-2">Símbolo</th>
                    <th className="text-left py-2">Dir</th>
                    <th className="text-right py-2">Precio</th>
                    <th className="text-right py-2">P&L</th>
                    <th className="text-center py-2">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => (
                    <tr key={t.id || i} className="border-b border-white/5">
                      <td className="py-2 text-gray-300">
                        {t.date ? new Date(t.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '-'}
                      </td>
                      <td className="py-2 text-white">{t.symbol}</td>
                      <td className="py-2">
                        <span className={t.direction === 'Short' ? 'text-red-400' : 'text-green-400'}>
                          {t.direction}
                        </span>
                      </td>
                      <td className="py-2 text-right text-gray-300">
                        ${t.entry_price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`py-2 text-right font-medium ${
                        t.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        ${t.pnl?.toLocaleString('en-US', { minimumFractionDigits: 2, signDisplay: 'always' })}
                      </td>
                      <td className="py-2 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          t.label === 'Mentor'
                            ? 'bg-blue-900/30 text-blue-400'
                            : 'bg-gray-700 text-gray-400'
                        }`}>
                          {t.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {trades.length >= 50 && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="mt-3 w-full py-2 text-sm text-gray-400 hover:text-white bg-white/5 rounded-lg transition-colors"
              >
                {loadingMore ? 'Cargando...' : 'Cargar más'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradingAccount;
